/**
 * App top bar: wordmark (home), family switcher (when >1), and the user menu.
 *
 * The language switcher does NOT live here — in-app, language is changed in
 * Settings (Nastavenia). Pre-auth, the compact flag switcher lives on the
 * sign-in screen.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { initials } from '@solandia/core';
import { useI18n } from '../i18n';
import { useFamily } from '../family/FamilyContext';
import { useTour } from '../tour/TourContext';
import { Wordmark } from '../components/Logo';
import type { SessionUser } from '../auth/types';
import { signOut } from '../auth/authClient';

export function TopBar({ user }: { user: SessionUser }) {
  const { t } = useI18n();
  const { me, family, families, setActiveFamily } = useFamily();
  const { startTour } = useTour();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // The canonical, resolved display name lives on `me.user` (the DB value the
  // Settings screen edits — never empty / "unknown"). Fall back to the session
  // principal only if `me` is somehow absent.
  const displayName = me?.user.displayName || user.displayName;
  const email = me?.user.email || user.email;

  // Close the menu on navigation (e.g. after opening Settings).
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <header className="topbar">
      <div className="container topbar-inner">
        <Link to="/" aria-label={`${t.appName} — domov`} className="wordmark">
          <Wordmark />
        </Link>

        <div className="row">
          {families.length > 1 ? (
            <label className="row text-sm">
              <span className="sr-only">{t.family.switcher}</span>
              <select
                className="select"
                style={{ minHeight: 40, maxWidth: 180 }}
                value={family.id}
                onChange={(e) => setActiveFamily(e.target.value)}
              >
                {families.map((m) => (
                  <option key={m.family.id} value={m.family.id}>
                    {m.family.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <button
            type="button"
            className="icon-button tour-help"
            aria-label={t.tour.help}
            title={t.tour.help}
            onClick={() => startTour()}
          >
            <span aria-hidden="true">?</span>
          </button>

          <div className="user-menu" data-tour="user-menu" ref={menuRef}>
            <button
              type="button"
              className="user-button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span className="avatar" aria-hidden="true">
                {initials(displayName)}
              </span>
              <span className="user-name">{displayName}</span>
            </button>
            {menuOpen ? (
              <div className="menu-popover" role="menu">
                <div className="menu-meta">
                  {t.auth.signedInAs}
                  <br />
                  <strong>{email || displayName}</strong>
                </div>
                <Link
                  to="/settings"
                  className="menu-item"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                >
                  {t.nav.settings}
                </Link>
                <button
                  type="button"
                  className="menu-item"
                  role="menuitem"
                  onClick={signOut}
                >
                  {t.auth.signOut}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}
